/**
 * Step 3 - Opis firmy
 * Wymagane: Nazwa firmy, Co oferujesz, Dla kogo, Gdzie działasz
 * Opcjonalne: Wyróżnik, styl komunikacji
 * 
 * PREMIUM VERSION V2 - Consistent with Step 2 style, better guidance
 */
import React from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Building2, Package, Users, MapPin, Sparkles, Check, Info } from 'lucide-react';

const FIELDS = [
  {
    id: 'businessName',
    label: 'Nazwa firmy / hodowli',
    placeholder: 'np. Hodowla Złote Łapy',
    required: true,
    type: 'input',
    icon: Building2
  },
  {
    id: 'businessOffer',
    label: 'Co oferujesz?',
    placeholder: 'np. Szczenięta rasy Golden Retriever z rodowodem, z hodowli domowej, socjalizowane od pierwszych dni życia',
    required: true,
    type: 'textarea',
    hint: 'Opisz krótko swoje produkty lub usługi',
    icon: Package
  },
  {
    id: 'targetAudience',
    label: 'Dla kogo?',
    placeholder: 'np. Rodziny z dziećmi szukające przyjaznego psa do domu',
    required: true,
    type: 'input',
    hint: 'Kto jest Twoim idealnym klientem?',
    icon: Users
  },
  {
    id: 'location',
    label: 'Gdzie działasz?',
    placeholder: 'np. Warszawa i okolice, wysyłka w całej Polsce',
    required: true,
    type: 'input',
    icon: MapPin
  },
  {
    id: 'differentiator',
    label: 'Co Cię wyróżnia?',
    placeholder: 'np. 15 lat doświadczenia, własny weterynarz, dożywotnia opieka nad szczeniętami',
    required: false,
    type: 'textarea',
    hint: 'To pomoże AI wyróżnić Cię na tle konkurencji',
    icon: Sparkles
  }
];

export default function StepBusiness({ value = {}, onChange }) {
  const updateField = (fieldId, val) => {
    onChange({
      ...value,
      [fieldId]: val
    });
  };
  
  // Count filled required fields
  const requiredFields = FIELDS.filter(f => f.required);
  const filledRequired = requiredFields.filter(f => value[f.id]?.trim()).length;
  const allRequiredFilled = filledRequired === requiredFields.length;
  
  // Count filled optional fields
  const optionalFields = FIELDS.filter(f => !f.required);
  const filledOptional = optionalFields.filter(f => value[f.id]?.trim()).length;
  
  return (
    <div className="space-y-8">
      {/* HEADER */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
          Opowiedz o swojej firmie
        </h1>
        <p className="text-base text-gray-600">
          Dzięki temu Booster dobierze treści pod Twoją ofertę i klientów.
        </p>
      </div>
      
      {/* MAIN FORM CARD */}
      <div className={`
        bg-white rounded-2xl p-6 transition-all duration-300
        ${allRequiredFilled 
          ? 'border-2 border-green-400 shadow-lg shadow-green-100/50' 
          : 'border-2 border-gray-200 shadow-md hover:shadow-lg'
        }
      `}>
        {/* Header with progress */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`
              w-12 h-12 rounded-xl flex items-center justify-center transition-all
              ${allRequiredFilled 
                ? 'bg-green-500 shadow-lg shadow-green-300/50' 
                : 'bg-gradient-to-br from-orange-500 to-orange-600 shadow-md shadow-orange-300/30'
              }
            `}>
              {allRequiredFilled ? (
                <Check className="w-6 h-6 text-white" strokeWidth={3} />
              ) : (
                <Building2 className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Podstawowe informacje</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Wypełnij {requiredFields.length} wymagane pola
              </p>
            </div>
          </div>
          
          {/* Progress badge */}
          <div className={`
            px-3 py-1.5 text-sm font-bold rounded-full transition-colors
            ${allRequiredFilled 
              ? 'bg-green-100 text-green-700' 
              : filledRequired > 0 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-gray-100 text-gray-500'
            }
          `}>
            {filledRequired} / {requiredFields.length}
          </div>
        </div>
        
        {/* Form fields */}
        <div className="space-y-5">
          {FIELDS.map((field) => {
            const Icon = field.icon;
            const isFilled = value[field.id]?.trim();
            
            return (
              <div key={field.id}>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <span className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${isFilled ? 'text-green-500' : 'text-orange-500'}`} />
                    {field.label}
                    {field.required ? (
                      <span className="text-red-500">*</span>
                    ) : (
                      <span className="font-normal text-gray-400">(opcjonalnie)</span>
                    )}
                    {isFilled && field.required && (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </span>
                </label>
                
                {field.type === 'textarea' ? (
                  <Textarea
                    placeholder={field.placeholder}
                    value={value[field.id] || ''}
                    onChange={(e) => updateField(field.id, e.target.value)}
                    rows={3}
                    className="resize-none text-base rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 placeholder:text-gray-400"
                  />
                ) : (
                  <Input
                    placeholder={field.placeholder}
                    value={value[field.id] || ''}
                    onChange={(e) => updateField(field.id, e.target.value)}
                    className="h-12 text-base rounded-xl border-2 border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 placeholder:text-gray-400"
                  />
                )}
                
                {field.hint && (
                  <p className="text-xs text-gray-500 mt-1.5">{field.hint}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* TIP */}
      <div className="flex items-start gap-4 p-4 bg-orange-50/50 rounded-xl border border-orange-100">
        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
          <Info className="w-4 h-4 text-orange-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-orange-800 mb-0.5">
            Nie musisz pisać idealnie
          </p>
          <p className="text-sm text-orange-700 leading-relaxed">
            AI dopracuje treści na podstawie tego, co napiszesz. Lepiej napisać prosto i szczerze niż na siłę "marketingowo".
          </p>
        </div>
      </div>
    </div>
  );
}
